/**
 * Detecção de HDR (PQ / HLG / Dolby Vision) em MP4, no cliente.
 *
 * POR QUÊ: converter HDR→SDR (tone mapping) é a etapa mais cara do pipeline de
 * transcode. O app mobile entrega o vídeo já em SDR; no caminho web o arquivo
 * chega cru do celular, então recusamos aqui e orientamos a publicar pelo app.
 *
 * COMO: lê apenas o cabeçalho e a box `moov` via `File.slice()` — um arquivo de
 * 200 MB nunca é carregado inteiro. Dentro do `moov`, procura a box `colr`
 * (transfer characteristics 16 = PQ/smpte2084, 18 = HLG/arib-std-b67) e as
 * boxes de configuração Dolby Vision (`dvcC`/`dvvC`/`dvwC`), que cobrem os
 * perfis em que o `colr` pode não descrever a transferência.
 *
 * Como toda validação de cliente (ver `upload-policy.ts`), é UX e é BURLÁVEL —
 * a validação AUTORITATIVA é server-side (ffprobe no transcoder).
 */

/** Teto de leitura do `moov`: o real fica na casa de dezenas de KB. */
const MAX_MOOV_BYTES = 8 * 1024 * 1024;
/** Teto de boxes de nível 1 percorridas, para não varrer arquivo corrompido. */
const MAX_TOP_LEVEL_BOXES = 64;
/** Boxes de configuração Dolby Vision: a presença já indica HDR. */
const DOLBY_VISION_BOXES = ['dvcC', 'dvvC', 'dvwC'];

async function readSlice(file: File, start: number, end: number): Promise<Uint8Array> {
  return new Uint8Array(await file.slice(start, end).arrayBuffer());
}

function be32(b: Uint8Array, i: number): number {
  return ((b[i] << 24) | (b[i + 1] << 16) | (b[i + 2] << 8) | b[i + 3]) >>> 0;
}

function boxType(b: Uint8Array, i: number): string {
  return String.fromCharCode(b[i], b[i + 1], b[i + 2], b[i + 3]);
}

/** Percorre as boxes de nível 1 e devolve o conteúdo do `moov` (sem o cabeçalho). */
async function readMoov(file: File): Promise<Uint8Array | null> {
  let offset = 0;
  for (let n = 0; n < MAX_TOP_LEVEL_BOXES && offset + 8 <= file.size; n++) {
    const head = await readSlice(file, offset, offset + 16);
    if (head.length < 8) return null;
    let size = be32(head, 0);
    let headerSize = 8;
    if (size === 1) {
      // Tamanho de 64 bits (largesize) nos 8 bytes seguintes ao tipo.
      if (head.length < 16) return null;
      size = be32(head, 8) * 4294967296 + be32(head, 12);
      headerSize = 16;
    } else if (size === 0) {
      size = file.size - offset; // box se estende até o fim do arquivo
    }
    if (size < headerSize) return null; // cabeçalho inconsistente
    if (boxType(head, 4) === 'moov') {
      const end = Math.min(offset + size, offset + headerSize + MAX_MOOV_BYTES);
      return readSlice(file, offset + headerSize, end);
    }
    offset += size; // pula o `mdat` inteiro sem lê-lo
  }
  return null;
}

/**
 * Procura assinatura de HDR no `moov`.
 *
 * As boxes procuradas ficam aninhadas em `trak/mdia/minf/stbl/stsd/<codec>`, e
 * as sample entries do `stsd` têm cabeçalho próprio de tamanho variável. Em vez
 * de descer a árvore, casamos o tipo da box e validamos o cabeçalho (tamanho
 * plausível + `colour_type` conhecido) — o mesmo sinal, sem um parser completo.
 */
function hasHdrSignature(moov: Uint8Array): boolean {
  for (let i = 4; i + 8 <= moov.length; i++) {
    const type = boxType(moov, i);
    if (type === 'colr') {
      const size = be32(moov, i - 4);
      if (size < 12 || size > 32) continue; // não é cabeçalho de box `colr`
      const colourType = boxType(moov, i + 4);
      if (colourType !== 'nclx' && colourType !== 'nclc') continue;
      if (i + 12 > moov.length) continue;
      // colour_type(4) + primaries(2) + transfer(2): transfer em i+10.
      const transfer = (moov[i + 10] << 8) | moov[i + 11];
      if (transfer === 16 || transfer === 18) return true;
      continue;
    }
    if (DOLBY_VISION_BOXES.includes(type)) {
      const size = be32(moov, i - 4);
      if (size >= 8 && size <= 64) return true;
    }
  }
  return false;
}

/**
 * `true` quando o MP4 é HDR.
 *
 * Falha ABERTA de propósito: arquivo sem `moov` legível, container inesperado ou
 * erro de leitura devolvem `false`. Rejeitar por dúvida barraria vídeo SDR
 * válido, e o gate que realmente protege o custo é o server-side.
 */
export async function isHdrVideo(file: File): Promise<boolean> {
  try {
    const moov = await readMoov(file);
    return moov ? hasHdrSignature(moov) : false;
  } catch {
    return false;
  }
}
